import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { MessageCircleQuestion, Sparkles, Trash2, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const DEFAULT_DEADLINE = "2026-11-09T12:00:00Z";

export default function TeamQuestionsCard({ teamId }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState("");
  const [aiVerdict, setAiVerdict] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [sensitive, setSensitive] = useState(false);
  const [wellInformed, setWellInformed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deadline, setDeadline] = useState(DEFAULT_DEADLINE);

  const isAdmin = user?.role === "admin" || user?.role === "faculty";

  const load = async () => {
    try {
      const [q, p] = await Promise.all([
        api.get(`/teams/${teamId}/questions`).then((r) => r.data).catch(() => []),
        api.get("/programme").then((r) => r.data).catch(() => ({})),
      ]);
      setQuestions(q || []);
      setDeadline(p?.questions_deadline || DEFAULT_DEADLINE);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (teamId) load(); /* eslint-disable-next-line */ }, [teamId]);

  const runAiCheck = async () => {
    if (!text.trim()) return toast.error("Type a question first");
    setAiBusy(true);
    setAiVerdict(null);
    try {
      const { data } = await api.post(`/teams/${teamId}/questions/ai-check`, { question: text.trim() });
      setAiVerdict(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI check failed");
    } finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return toast.error("Type a question first");
    if (wellInformed) return toast.error("If you can answer this with a well-informed assumption, please don't ask the business");
    setSaving(true);
    try {
      await api.post(`/teams/${teamId}/questions`, {
        question: text.trim(),
        is_sensitive: sensitive,
        well_informed_assumption_possible: wellInformed,
        ai_check_result: aiVerdict?.message || null,
      });
      toast.success("Question added to your repository");
      setText(""); setAiVerdict(null); setSensitive(false); setWellInformed(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save question");
    } finally { setSaving(false); }
  };

  const del = async (q) => {
    if (!(await confirm({ title: "Delete this question?", destructive: true }))) return;
    try {
      await api.delete(`/teams/${teamId}/questions/${q.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const saveDeadline = async (newIso) => {
    try {
      await api.put("/programme", { questions_deadline: newIso });
      setDeadline(newIso);
      toast.success("Deadline updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update deadline");
    }
  };

  const deadlineDate = deadline ? new Date(deadline) : null;
  const passed = deadlineDate && deadlineDate.getTime() < Date.now();

  return (
    <div className="border border-[#0A0A0A] bg-white p-6 space-y-5" data-testid="team-questions-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><MessageCircleQuestion size={12} /> Questions for the business</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Compile your list</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl">
            Add any follow-up questions after Session 2. Admin will review and email the compiled list to the business.
            Only ask what you genuinely need — <strong>time is a resource for the business too</strong>.
          </p>
        </div>
        <div className={`text-xs px-3 py-2 border ${passed ? "border-[#A23232] bg-[#FDEDED] text-[#7A1F1F]" : "border-[#f4b847] bg-[#FEF9E7] text-[#7A5B17]"}`} data-testid="team-questions-deadline">
          <div className="flex items-center gap-1.5 label-eyebrow"><Clock size={11} /> {passed ? "Deadline passed" : "Deadline"}</div>
          <div className="font-display text-lg font-black tracking-tight">
            {deadlineDate ? deadlineDate.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </div>
          {isAdmin && (
            <input
              type="datetime-local"
              value={deadline ? new Date(deadline).toISOString().slice(0, 16) : ""}
              onChange={(e) => saveDeadline(new Date(e.target.value).toISOString())}
              className="mt-1 text-[10px] border border-[#f4b847] px-1 py-0.5 w-full"
              data-testid="team-questions-deadline-edit"
            />
          )}
        </div>
      </div>

      <form onSubmit={submit} className="border border-[#E5E7EB] p-4 space-y-3" data-testid="team-questions-form">
        <Textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setAiVerdict(null); }}
          placeholder="Type your question for the business…"
          className="rounded-none min-h-[80px]"
          data-testid="team-questions-input"
          disabled={passed && !isAdmin}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={runAiCheck}
            disabled={aiBusy || !text.trim()}
            data-testid="team-questions-ai-check-btn"
            className="rounded-none"
          >
            <Sparkles size={14} className="mr-1.5" /> {aiBusy ? "Checking…" : "Is this already answered in the earlier conversation?"}
          </Button>
        </div>

        {aiVerdict && (
          <div className={`text-xs p-3 border ${aiVerdict.verdict === "no_transcripts" ? "border-slate-300 bg-slate-50" : "border-[#2596be] bg-[#E0F2FF]"}`} data-testid="team-questions-ai-result">
            <div className="label-eyebrow mb-1 flex items-center gap-1.5"><Sparkles size={11} /> AI check</div>
            <div className="text-[#0A0A0A] whitespace-pre-wrap">{aiVerdict.message}</div>
          </div>
        )}

        <label className="flex items-start gap-2 text-xs" data-testid="team-questions-sensitive-label">
          <input type="checkbox" checked={sensitive} onChange={(e) => setSensitive(e.target.checked)} className="mt-0.5" data-testid="team-questions-sensitive"/>
          <span className="text-[#525252]"><strong>Commercially sensitive?</strong> Flag if the business shouldn't share this publicly.</span>
        </label>

        <label className="flex items-start gap-2 text-xs" data-testid="team-questions-wellinformed-label">
          <input type="checkbox" checked={wellInformed} onChange={(e) => setWellInformed(e.target.checked)} className="mt-0.5" data-testid="team-questions-wellinformed"/>
          <span className="text-[#525252]"><strong>Could you answer this with a well-informed assumption instead?</strong> If yes, please don't add — save the business's time.</span>
        </label>

        {wellInformed && (
          <div className="text-xs text-[#7A1F1F] bg-[#FDEDED] border border-[#E93E36] px-3 py-2 flex items-center gap-1.5" data-testid="team-questions-block-msg">
            <AlertTriangle size={12} /> Please make the assumption instead — this question won't be added.
          </div>
        )}

        <Button
          type="submit"
          disabled={saving || wellInformed || !text.trim() || (passed && !isAdmin)}
          data-testid="team-questions-submit-btn"
          className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]"
        >
          <CheckCircle2 size={14} className="mr-1.5" /> {saving ? "Saving…" : "Add to the list"}
        </Button>
      </form>

      <div>
        <div className="label-eyebrow mb-3">Your questions ({questions.length})</div>
        {questions.length === 0 ? (
          <div className="text-sm text-[#525252] italic">No questions yet. Add the first above.</div>
        ) : (
          <ul className="divide-y divide-[#E5E7EB]" data-testid="team-questions-list">
            {questions.map((q) => (
              <li key={q.id} className="py-3 flex items-start justify-between gap-3" data-testid={`team-question-${q.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {q.is_sensitive && (
                      <span className="text-[10px] uppercase tracking-wider bg-[#A23232] text-white px-1.5 py-0.5 font-bold">Sensitive</span>
                    )}
                    <p className="text-sm text-[#0A0A0A]">{q.question}</p>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider text-[#525252] mt-1">
                    {q.author_name} · {new Date(q.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  {q.ai_check_result && (
                    <div className="text-[11px] text-[#525252] mt-1 italic">AI check: {q.ai_check_result}</div>
                  )}
                </div>
                {(isAdmin || q.author_id === user?.id) && (
                  <button onClick={() => del(q)} className="text-[#A23232] hover:text-[#7A1F1F]" data-testid={`team-question-delete-${q.id}`} title="Delete">
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
